import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CNPatient } from '../models/cnpatient';
import { CNProtocol } from '../models/cnprotocol';
import { SNItemFactory } from '../models/snitemfactory';
import { BecareService } from './becare.service';
import { PatientService } from './patient.service';

export enum ProtocolServiceFilter {
  all = 'all'
}

export class ProtocolServiceSubject {
  private becareService: BecareService;
  private myPatient: CNPatient;
  private myName: string;
  private myFilter: ProtocolServiceFilter;
  private paged: boolean | number;
  private buffer: CNProtocol[];
  public protocols: BehaviorSubject<CNProtocol[]>;
  public myStart: number;
  public myLimit: number;
  public mySearch: string;
  public length: number;

  private protocolUrl = '';

  constructor(becare: BecareService, patient: CNPatient, name: string, filter: ProtocolServiceFilter, paged: boolean | number) {
    this.becareService = becare;
    this.myName = name;
    this.myFilter = filter;
    this.paged = paged;
    if (paged) {
      this.myStart = 0;
      this.myLimit = 10;
      this.length = 0;
    }
    this.protocols = new BehaviorSubject<CNProtocol[]>([]);
    // Set patient, build rest url and query first buffer
    this.patient = patient;
  }

  private load() {
    if (!this.protocolUrl) {
      return;
    }
    this.becareService.call(this.protocolUrl).subscribe(result => {
      if (result.success === true && result.protocols !== undefined) {
        const factory = new SNItemFactory();
        this.buffer = factory.itemsFromJson(result.protocols);
        this.length = result.total;
        this.protocols.next(this.buffer);
      }
    });
  }

  private buildUrl() {
    if (this.paged) {
      this.protocolUrl = '/fr/patient/' + this.myPatient.id + '/protocols?start=' + this.myStart + '&limit=' + this.myLimit;
      if (this.mySearch) {
        this.protocolUrl += '&search=' + encodeURIComponent(this.mySearch);
      }
    } else {
      // switch (this.myFilter) {
      //   case ProtocolServiceSubject.active:
      //     this.problemUrl = '/fr/patient/' + this.myPatient.id + '/problems/active';
      //     break;
      //   case ProtocolServiceSubject.notactive:
      //     this.problemUrl = '/fr/patient/' + this.myPatient.id + '/problems/notactive';
      //     break;
      //   default:
      //     this.problemUrl = '';
      // }
    }
  }

  clear(): void {
    this.buffer = [];
    this.length = 0;
    this.protocols.next([]);
  }

  set patient(patient: CNPatient) {
    this.myPatient = patient;
    if (!this.myPatient) {
      return;
    }
    this.buildUrl();
    this.load();
  }

  set start(start: number) {
    this.myStart = start;
    this.buildUrl();
    this.load();
  }

  set limit(limit: number) {
    this.myLimit = limit;
    this.buildUrl();
    this.load();
  }

  get limit(): number {
    return this.myLimit;
  }

  set search(query: string) {
    this.mySearch = query;
    this.buildUrl();
    this.load();
  }

  query(start: number, limit: number, search: string) {
    this.myStart = start;
    this.myLimit = limit;
    this.mySearch = search;
    this.buildUrl();
    this.load();
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProtocolService {

  private static instance: ProtocolService = null;
  private currentpatient: CNPatient;
  @Output() clear = new EventEmitter<boolean>();

  private subjects: Map<string, ProtocolServiceSubject> = new Map();

  constructor(private becare: BecareService, private patient: PatientService) {
    ProtocolService.instance = this;
    this.patient.patientSelectedEvent.subscribe(patientselected => {
      this.currentpatient = patientselected;
      this.subjects.forEach((subject: ProtocolServiceSubject, name: string) => {
        subject.patient = this.currentpatient;
      });
      this.clear.emit(true);
    });
    // Initiate with current selected patient
    this.currentpatient = this.patient.patient;
  }

  public static getInstance(): ProtocolService {
    return ProtocolService.instance;
  }

  public getSubject(name: string, filter?: ProtocolServiceFilter, paged?: boolean | number): ProtocolServiceSubject {
    if (this.subjects.has(name)) {
      return this.subjects.get(name);
    }
    if (!filter && !paged) {
      return undefined;
    }
    // Build new service subject
    const newSubject = new ProtocolServiceSubject(this.becare, this.currentpatient, name, filter, paged);
    this.subjects.set(name, newSubject);
    return newSubject;
  }

  public protocolThumbnails(patient: CNPatient, protocol: CNProtocol): Observable<string[]> {
    return new Observable(observer => {
      const url = '/fr/patient/' + patient.id + '/protocol/' + protocol.id + '/thumbnails';
      this.becare.call(url).subscribe(result => {
        if (result.success === true && result.thumbnails !== undefined) {
          observer.next(result.thumbnails);
        }
      });
    });
  }

  public protocolDocuments(patient: CNPatient, protocol: CNProtocol): Observable<string[]> {
    return new Observable(observer => {
      const url = '/fr/patient/' + patient.id + '/protocol/' + protocol.id + '/documents';
      this.becare.call(url).subscribe(result => {
        if (result.success === true && result.documents !== undefined) {
          observer.next(result.documents);
        }
      });
    });
  }

  public ocr(patient: CNPatient, protocol: CNProtocol): Observable<string[]> {
    return new Observable(observer => {
      const url = '/fr/patient/' + patient.id + '/protocol/' + protocol.id + '/ocr';
      this.becare.call(url).subscribe(result => {
        if (result.success === true && result.result !== undefined) {
          observer.next(result.result);
        }
      });
    });
  }
}
